import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter } from "../prizes.js";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { offerAvailable, claimReward } from "../rewards.js";

/* level display name: "Level 1", "Level 2", "Finalen"/"The Final" */
const lvlName = (i) => (i === LEVELS_COUNT - 1 ? t("b_final") : t("b_level") + " " + (i + 1));
const LEVELS_COUNT = 3;

/* ================= BASKETBALL – beat the clock, 3 levels ======================
   Same ball mechanics as the horse race: take a ball from the tray, place it
   between the lines, drag back to shoot over the edge. One moving hoop instead
   of holes. 30 seconds per level: hit 3, then 4, then 5. The hoop slides on
   level 2 and the finale. Clear all three levels → a MEDIUM plush, always.
================================================================================ */

const W = 360, H = 540;
const BALL_R = 10;
const START_Y = H - 76;
const ZONE_TOP = H - 110;
const ZONE_BOT = START_Y + BALL_R + 4;
const PLACE_MIN_Y = ZONE_TOP + BALL_R + 2;
const PLACE_MAX_Y = ZONE_BOT - BALL_R - 2;
const TRAY = { x: W - 38, y: H - 30, r: 30 };
const GRAB_R = 26;               // near-ball drags decide by direction: down = shoot, sideways/up = move

const EDGE_Y = 352;
const EDGE_MIN = 170;
const AIR_TIME = 0.32;   // max balls per level: LEVELS[*].balls

const HOOP_Y = 120;
const HOOP_R = 19;
const HOOP_AMP = 100;            // how far the hoop slides from centre
const LEVEL_TIME = 30;           // seconds per level

const LEVELS = [
  { need: 3, freq: 0,   balls: 2, label: "Level 1" },
  { need: 4, freq: 0.9, balls: 2, label: "Level 2" },
  { need: 5, freq: 1.5, balls: 3, label: "Finalen" },   // extra ball for the finale
];

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(out()); o.start(t); o.stop(t + dur);
  };
  return {
    unlock: ensure,
    launch() { const c = ensure(); if (c) tone(180, c.currentTime, 0.12, "triangle", 0.1); },
    knock() { const c = ensure(); if (c) tone(300, c.currentTime, 0.06, "square", 0.08); },
    swish() {
      const c = ensure(); if (!c) return;
      tone(620, c.currentTime, 0.16, "sine", 0.16);
      tone(930, c.currentTime + 0.08, 0.2, "sine", 0.1);
    },
    levelup() {
      const c = ensure(); if (!c) return;
      [440, 554, 659].forEach((f, i) => tone(f, c.currentTime + i * 0.1, 0.22, "triangle", 0.13));
    },
    buzzer() { const c = ensure(); if (c) tone(110, c.currentTime, 0.7, "sawtooth", 0.16); },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
  };
}

export default function Basketball({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | failed | finished
  const [hud, setHud] = useState({ level: 0, hits: 0 });
  const [failedAt, setFailedAt] = useState(0);
  const [claimed, setClaimed] = useState(null);
  const [offer, setOffer] = useState(false);     // rewarded "retry the level" offer
  const [adErr, setAdErr] = useState(false);
  const gameRef = useRef(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setHud({ level: 0, hits: 0 });
    setClaimed(null);
    setOffer(false);
    setAdErr(false);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* time ran out ONE basket short: a video restarts the same level with a
     fresh clock (max once per game). Prize here is always medium, so there
     is no trophy to guard. */
  const declineOffer = () => {
    setOffer(false);
    const g = gameRef.current;
    if (!g) return;
    setFailedAt(g.level);
    setPhase("failed");
  };
  const watchAd = () => {
    setAdErr(false);
    claimReward("ekstra_level", {
      onReward: () => {
        const g = gameRef.current;
        if (!g) { setOffer(false); return; }
        g.hits = 0;
        g.time = LEVEL_TIME;
        g.balls = g.balls.filter((x) => x.phase === "placed");
        g.banner = {
          txt: g.level === LEVELS.length - 1
            ? t("b_banner3", { n: LEVELS[g.level].need })
            : t("b_banner2", { level: lvlName(g.level).toUpperCase(), n: LEVELS[g.level].need }),
          t: 0,
        };
        g.freeze = 1.6;
        g.over = false;
        setHud({ level: g.level, hits: 0 });
        setOffer(false);
      },
      onNoReward: (reason) => { if (reason !== "dismissed") setAdErr(true); },
    });
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      balls: [],
      carry: null,
      pending: null,
      aim: null,
      popups: [],
      level: 0,
      hits: 0,
      time: LEVEL_TIME,
      hoopX: W / 2,
      banner: null,              // {txt, t}
      freeze: 0,                 // countdown pause between levels
      retryOffered: false,       // ad-retry: max once per game
      over: false,
      now: 0,
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__bb = g;
    g.banner = { txt: t("b_banner1", { n: LEVELS[0].need }), t: 0 };
    g.freeze = 1.4;

    /* ---- canvas sizing (same as horse race) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input (identical to horse race) ---- */
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) / (r.width / W), y: (t.clientY - r.top) / (r.height / H) };
    };
    const maxBalls = () => LEVELS[g.level].balls;
    const trayCount = () => maxBalls() - g.balls.length - (g.carry ? 1 : 0);
    const down = (e) => {
      audioRef.current.unlock();
      if (g.over) return;
      e.preventDefault();
      const p = pos(e);
      const pb = g.balls.find((b) => b.phase === "placed" && Math.hypot(p.x - b.x, p.y - b.y) < GRAB_R);
      if (pb) {
        g.pending = { ball: pb, sx: p.x, sy: p.y };
        return;
      }
      if (trayCount() > 0 && Math.hypot(p.x - TRAY.x, p.y - TRAY.y) < TRAY.r + 8) {
        g.carry = { x: p.x, y: p.y };
        return;
      }
      const placed = g.balls.filter((b) => b.phase === "placed");
      if (placed.length) {
        const target = placed.reduce((a, b) =>
          Math.hypot(p.x - a.x, p.y - a.y) <= Math.hypot(p.x - b.x, p.y - b.y) ? a : b);
        g.aim = { ball: target, sx: p.x, sy: p.y, dx: 0, dy: 0 };
      }
    };
    const clampCarry = (p) => ({
      x: Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, p.x)),
      y: Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, p.y)),   // stays in the zone: drop = what you see
    });
    const move = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      const p = pos(e);
      if (g.pending) {
        const dx = p.x - g.pending.sx, dy = p.y - g.pending.sy;
        if (Math.hypot(dx, dy) > 12) {
          const ball = g.pending.ball;
          if (dy > Math.abs(dx)) {
            g.aim = { ball, sx: g.pending.sx, sy: g.pending.sy, dx, dy };
          } else {
            g.balls = g.balls.filter((b) => b !== ball);
            g.carry = clampCarry(p);
          }
          g.pending = null;
        }
        return;
      }
      if (g.aim) {
        g.aim.dx = p.x - g.aim.sx;
        g.aim.dy = p.y - g.aim.sy;
      } else {
        const c = clampCarry(p);
        g.carry.x = c.x; g.carry.y = c.y;
      }
    };
    const up = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      if (g.pending) { g.pending = null; return; }   // a plain tap: ball stays put
      if (g.carry) {
        let x = Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, g.carry.x));
        const y = Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, g.carry.y));
        g.carry = null;
        for (const b of g.balls)
          if (b.phase === "placed" && Math.hypot(b.x - x, b.y - y) < BALL_R * 2)
            x = b.x + (x >= b.x ? BALL_R * 2 + 1 : -(BALL_R * 2 + 1));
        g.balls.push({ x, y, vx: 0, vy: 0, phase: "placed", air: null, sink: null });
        return;
      }
      const { ball, dx, dy } = g.aim;
      g.aim = null;
      const len = Math.hypot(dx, dy);
      if (len < 14 || dy < 6) return;
      const power = Math.min(len, 190) / 190;
      const ang = Math.atan2(-dy, -dx);
      const speed = 260 + power * 620;
      ball.vx = Math.cos(ang) * speed;
      ball.vy = Math.sin(ang) * speed;
      ball.phase = "ramp";
      audioRef.current.launch();
    };
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", up, { passive: false });

    /* ---- scoring / level flow ---- */
    const basket = (b) => {
      g.hits += 1;
      g.popups.push({ x: g.hoopX, y: HOOP_Y - 26, txt: t("pop_basket"), c: "#E8722A", t: 0 });
      audioRef.current.swish();
      const lvl = LEVELS[g.level];
      if (g.hits >= lvl.need) {
        if (g.level === LEVELS.length - 1) {
          g.over = true;
          audioRef.current.win();
          setTimeout(() => setPhase("finished"), 900);
        } else {
          g.level += 1;
          g.hits = 0;
          g.time = LEVEL_TIME;
          g.balls = g.balls.filter((x) => x.phase === "placed");  // loose balls back to the tray
          g.banner = {
            txt: g.level === LEVELS.length - 1
              ? t("b_banner3", { n: LEVELS[g.level].need })
              : t("b_banner2", { level: lvlName(g.level).toUpperCase(), n: LEVELS[g.level].need }),
            t: 0,
          };
          g.freeze = 1.6;
          audioRef.current.levelup();
        }
      }
      setHud({ level: g.level, hits: g.hits });
    };

    /* ---- physics (same as horse race, one moving hoop instead of holes) ---- */
    // the backboard is solid: balls bank off it instead of passing through
    const BB_HW = 34, BB_TOP = HOOP_Y - 40, BB_BOT = HOOP_Y - 30;
    const bounceBackboard = (b) => {
      const cy = (BB_TOP + BB_BOT) / 2, hh = (BB_BOT - BB_TOP) / 2;
      const penX = BB_HW + BALL_R - Math.abs(b.x - g.hoopX);
      const penY = hh + BALL_R - Math.abs(b.y - cy);
      if (penX <= 0 || penY <= 0) return;
      if (penX < penY) {
        const s = b.x >= g.hoopX ? 1 : -1;
        b.x = g.hoopX + s * (BB_HW + BALL_R);
        if (b.vx * s < 0) { b.vx = s * Math.abs(b.vx) * 0.6; audioRef.current.knock(); }
      } else {
        const s = b.y >= cy ? 1 : -1;
        b.y = cy + s * (hh + BALL_R);
        if (b.vy * s < 0) { b.vy = s * Math.abs(b.vy) * 0.55; audioRef.current.knock(); }
      }
    };

    const stepBall = (b, dt) => {
      if (b.phase === "placed") return;
      if (b.phase === "sinking") {
        b.sink.t += dt;
        if (b.sink.t > 0.55) b.dead = true;
        return;
      }
      if (b.phase === "air") {
        b.air.t += dt;
        b.x += b.vx * dt; b.y += b.vy * dt;
        if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
        if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }
        if (b.y < BALL_R + 8) { b.y = BALL_R + 8; b.vy = Math.abs(b.vy) * 0.5; }
        bounceBackboard(b);
        if (b.air.t >= AIR_TIME) { b.phase = "board"; b.air = null; }
        return;
      }
      const onBoard = b.phase === "board";
      b.vy += (onBoard ? 130 : 150) * dt;
      const damp = Math.exp(-(onBoard ? 1.15 : 0.55) * dt);
      b.vx *= damp; b.vy *= damp;
      b.x += b.vx * dt; b.y += b.vy * dt;

      if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
      if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }
      if (b.y < BALL_R + 8) { b.y = BALL_R + 8; b.vy = Math.abs(b.vy) * 0.5; }

      const spd = Math.hypot(b.vx, b.vy);

      if (!onBoard && b.y <= EDGE_Y) {
        if (b.vy < 0 && spd >= EDGE_MIN) {
          b.phase = "air";
          b.air = { t: 0 };
        } else {
          b.y = EDGE_Y;
          b.vy = Math.abs(b.vy) * 0.4;
        }
        return;
      }
      if (onBoard && b.y >= EDGE_Y - BALL_R + 2) {
        b.y = EDGE_Y - BALL_R + 2;
        if (b.vy > 0) b.vy = -b.vy * 0.2;
        b.edgeT = (b.edgeT || 0) + dt;
        if (b.edgeT > 0.5) { b.dead = true; return; }
      }

      if (onBoard) bounceBackboard(b);

      if (onBoard && !g.over) {
        const d = Math.hypot(b.x - g.hoopX, b.y - HOOP_Y);
        if (d < HOOP_R - 2 && spd < 190) {
          b.phase = "sinking";
          b.sink = { hole: { x: g.hoopX, y: HOOP_Y }, t: 0 };
          basket(b);
        } else if (d < HOOP_R + 2 && spd >= 190) {
          b.vx += (b.x - g.hoopX) * 2.2; b.vy += (b.y - HOOP_Y) * 2.2;
          b.vx *= 0.94; b.vy *= 0.94;
        }
      }

      if (b.phase === "ramp" && b.y > H - 30 && b.vy > 0) b.dead = true;
    };

    const step = (dt) => {
      g.now += dt;
      const lvl = LEVELS[g.level];

      // hoop slides on level 2 and the finale
      g.hoopX = W / 2 + (lvl.freq ? Math.sin(g.now * lvl.freq) * HOOP_AMP : 0);

      if (g.freeze > 0) {
        g.freeze -= dt;
      } else if (!g.over) {
        g.time -= dt;
        if (g.time <= 0) {
          g.time = 0;
          g.over = true;
          audioRef.current.buzzer();
          // ONE basket short: offer a retry of the level for a video
          if (!g.retryOffered && g.hits >= lvl.need - 1 && offerAvailable("ekstra_level")) {
            g.retryOffered = true;
            setOffer(true);
          } else {
            setFailedAt(g.level);
            setTimeout(() => setPhase("failed"), 900);
          }
        }
      }

      for (const b of g.balls) stepBall(b, dt);

      for (let i = 0; i < g.balls.length; i++) {
        for (let j = i + 1; j < g.balls.length; j++) {
          const a = g.balls[i], b = g.balls[j];
          if (a.phase === "air" || a.phase === "sinking") continue;
          if (b.phase === "air" || b.phase === "sinking") continue;
          const dx = b.x - a.x, dy = b.y - a.y;
          const d = Math.hypot(dx, dy);
          if (d <= 0 || d >= BALL_R * 2) continue;
          const nx = dx / d, ny = dy / d;
          const overlap = (BALL_R * 2 - d) / 2;
          a.x -= nx * overlap; a.y -= ny * overlap;
          b.x += nx * overlap; b.y += ny * overlap;
          const p = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;
          if (p > 0) {
            a.vx -= p * nx; a.vy -= p * ny;
            b.vx += p * nx; b.vy += p * ny;
            if (a.phase === "placed") a.phase = "ramp";
            if (b.phase === "placed") b.phase = "ramp";
            audioRef.current.knock();
          }
        }
      }

      g.balls = g.balls.filter((b) => !b.dead);
      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);
      if (g.banner) { g.banner.t += dt; if (g.banner.t > 1.8) g.banner = null; }
    };

    /* ---- static backdrop, baked once (2x offscreen): parquet court, painted
       lines, edge step, zone lines, spotlight/vignette, steel tray, frame ---- */
    const rr = (c, x, y, w, h, r) => {
      c.beginPath();
      c.moveTo(x + r, y);
      c.arcTo(x + w, y, x + w, y + h, r);
      c.arcTo(x + w, y + h, x, y + h, r);
      c.arcTo(x, y + h, x, y, r);
      c.arcTo(x, y, x + w, y, r);
      c.closePath();
    };
    const rnd = (i) => { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);

      // warm oak base
      const base = b.createLinearGradient(0, 0, 0, H);
      base.addColorStop(0, "#7c5028");
      base.addColorStop(0.5, "#a06e3c");
      base.addColorStop(1, "#8c5e32");
      b.fillStyle = base; b.fillRect(0, 0, W, H);

      // vertical planks: tone variation, grain streaks, staggered joints
      const PW = 27;
      for (let i = 0, x = 0; x < W; x += PW, i++) {
        const tone = rnd(i) * 0.15 - 0.075;
        b.fillStyle = tone > 0 ? `rgba(255,224,168,${tone})` : `rgba(46,22,4,${-tone})`;
        b.fillRect(x, 0, PW, H);
        b.strokeStyle = "rgba(70,38,12,0.12)";
        b.lineWidth = 1;
        for (let k = 0; k < 3; k++) {
          const gx = x + 4 + rnd(i * 7 + k) * (PW - 8);
          b.beginPath(); b.moveTo(gx, -4);
          for (let y = 0; y <= H + 24; y += 24)
            b.lineTo(gx + Math.sin(y * 0.028 + i * 1.7 + k * 2.3) * 1.6, y);
          b.stroke();
        }
        b.fillStyle = "rgba(46,22,4,0.25)";
        let jy = rnd(i * 13 + 5) * 110;
        while (jy < H) { b.fillRect(x + 1, jy, PW - 1, 1.5); jy += 80 + rnd(i * 31 + jy) * 90; }
        b.fillStyle = "rgba(46,22,4,0.18)";
        b.fillRect(x, 0, 1, H);
      }

      // painted half-court markings, worn cream (baseline at the top, by the hoop)
      b.lineCap = "round";
      b.strokeStyle = "rgba(246,238,217,0.30)";
      b.lineWidth = 3;
      b.strokeRect(14, 14, W - 28, H - 28);                                  // boundary
      const KEY_HW = 58, FT_Y = 190;                                          // the key (paint)
      b.beginPath();
      b.moveTo(W / 2 - KEY_HW, 14); b.lineTo(W / 2 - KEY_HW, FT_Y);
      b.moveTo(W / 2 + KEY_HW, 14); b.lineTo(W / 2 + KEY_HW, FT_Y);
      b.moveTo(W / 2 - KEY_HW, FT_Y); b.lineTo(W / 2 + KEY_HW, FT_Y);         // free-throw line
      b.stroke();
      for (let k = 0; k < 3; k++) {                                           // lane hash marks
        const hy = 64 + k * 38;
        b.beginPath();
        b.moveTo(W / 2 - KEY_HW, hy); b.lineTo(W / 2 - KEY_HW - 9, hy);
        b.moveTo(W / 2 + KEY_HW, hy); b.lineTo(W / 2 + KEY_HW + 9, hy);
        b.stroke();
      }
      b.beginPath(); b.arc(W / 2, FT_Y, 46, 0, Math.PI); b.stroke();          // free-throw circle: solid lower half
      b.setLineDash([7, 8]);
      b.beginPath(); b.arc(W / 2, FT_Y, 46, Math.PI, Math.PI * 2); b.stroke(); // dashed upper half
      b.setLineDash([]);
      b.beginPath();                                                          // three-point line
      b.moveTo(26, 14); b.lineTo(26, 120);
      b.arc(W / 2, 120, W / 2 - 26, Math.PI, Math.PI * 2, true);              // arc around the key
      b.lineTo(W - 26, 14);
      b.stroke();

      // edge step between plate and ramp
      const eg = b.createLinearGradient(0, EDGE_Y - 26, 0, EDGE_Y);
      eg.addColorStop(0, "rgba(0,0,0,0)");
      eg.addColorStop(1, "rgba(0,0,0,0.30)");
      b.fillStyle = eg; b.fillRect(6, EDGE_Y - 26, W - 12, 26);
      b.fillStyle = "#5a3418"; b.fillRect(6, EDGE_Y, W - 12, 3);
      b.fillStyle = "rgba(255,232,190,0.28)"; b.fillRect(6, EDGE_Y + 3, W - 12, 2);

      // placement zone: painted guide lines
      b.strokeStyle = "rgba(246,238,217,0.45)";
      b.lineWidth = 2;
      b.setLineDash([6, 6]);
      b.beginPath(); b.moveTo(10, ZONE_TOP); b.lineTo(W - 10, ZONE_TOP); b.stroke();
      b.strokeStyle = "rgba(246,238,217,0.28)";
      b.setLineDash([3, 8]);
      b.beginPath(); b.moveTo(12, ZONE_BOT); b.lineTo(W - 12, ZONE_BOT); b.stroke();
      b.setLineDash([]);

      // spotlight over the court + dark vignette
      const sp = b.createRadialGradient(W / 2, H * 0.42, 70, W / 2, H * 0.46, 430);
      sp.addColorStop(0, "rgba(255,234,186,0.10)");
      sp.addColorStop(0.45, "rgba(0,0,0,0)");
      sp.addColorStop(1, "rgba(20,10,3,0.52)");
      b.fillStyle = sp; b.fillRect(0, 0, W, H);

      // steel ball tray sunk into the floor
      b.fillStyle = "rgba(0,0,0,0.35)";
      rr(b, 282, 486, 74, 50, 12); b.fill();
      const trg = b.createLinearGradient(0, 483, 0, 533);
      trg.addColorStop(0, "#e3e7ea"); trg.addColorStop(0.5, "#9aa1a8"); trg.addColorStop(1, "#c9ced3");
      b.fillStyle = trg;
      rr(b, 284, 483, 72, 50, 11); b.fill();
      const tin = b.createLinearGradient(0, 488, 0, 528);
      tin.addColorStop(0, "#22262a"); tin.addColorStop(1, "#3a4046");
      b.fillStyle = tin;
      rr(b, 289, 488, 62, 40, 8); b.fill();
      b.strokeStyle = "rgba(255,255,255,0.18)"; b.lineWidth = 1;
      rr(b, 289, 488, 62, 40, 8); b.stroke();

      // mahogany frame with a brass pinstripe
      b.fillStyle = "#3f2412";
      b.fillRect(0, 0, 6, H); b.fillRect(W - 6, 0, 6, H);
      b.fillRect(0, 0, W, 8); b.fillRect(0, H - 6, W, 6);
      b.fillStyle = "rgba(255,255,255,0.10)";
      b.fillRect(0, 0, 2, H); b.fillRect(0, 0, W, 2);
      b.fillStyle = "rgba(201,162,75,0.8)";
      b.fillRect(6, 8, 1, H - 14); b.fillRect(W - 7, 8, 1, H - 14);
      b.fillRect(6, 8, W - 12, 1); b.fillRect(6, H - 7, W - 13, 1);
    }

    /* ---- drawing ---- */
    const drawBallShape = (x, y, r) => {
      // a little basketball: orange with seams
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = "#E8722A"; ctx.fill();
      ctx.strokeStyle = "rgba(60,25,5,0.55)"; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x - r, y); ctx.lineTo(x + r, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - r); ctx.lineTo(x, y + r); ctx.stroke();
      ctx.beginPath(); ctx.arc(x - r * 1.3, y, r * 1.15, -0.6, 0.6); ctx.stroke();
      ctx.beginPath(); ctx.arc(x + r * 1.3, y, r * 1.15, Math.PI - 0.6, Math.PI + 0.6); ctx.stroke();
      ctx.lineWidth = 1;
    };

    const draw = () => {
      // baked backdrop: court, painted lines, edge, zone, vignette, tray, frame
      ctx.drawImage(bg, 0, 0, W, H);

      // placement zone lights up while carrying
      if (g.carry) {
        ctx.fillStyle = "rgba(255,236,190,0.10)";
        ctx.fillRect(8, ZONE_TOP, W - 16, ZONE_BOT - ZONE_TOP);
      }

      // HUD: glass panel with timer + hits
      const lvl = LEVELS[g.level];
      ctx.fillStyle = "rgba(22,13,6,0.42)";
      rr(ctx, 34, 14, W - 68, 68, 12); ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.14)"; ctx.lineWidth = 1;
      rr(ctx, 34, 14, W - 68, 68, 12); ctx.stroke();
      ctx.textAlign = "center";
      ctx.font = "bold 30px 'Alfa Slab One', Georgia, serif";
      const tcol = g.time <= 5 ? "#ff5a4e" : "#fff";
      ctx.lineWidth = 5; ctx.strokeStyle = "rgba(0,0,0,0.45)";
      ctx.strokeText(Math.ceil(g.time) + "", W / 2, 46);
      ctx.fillStyle = tcol;
      ctx.fillText(Math.ceil(g.time) + "", W / 2, 46);
      ctx.font = "800 13px system-ui, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillText(lvlName(g.level) + "  ·  " + g.hits + " / " + lvl.need, W / 2, 63);
      // time bar
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(46, 71, W - 92, 6);
      ctx.fillStyle = tcol;
      ctx.fillRect(46, 71, (W - 92) * (g.time / LEVEL_TIME), 6);

      // backboard + hoop (drawn in front of the HUD glass)
      ctx.fillStyle = "rgba(0,0,0,0.22)";
      ctx.fillRect(g.hoopX - 32, HOOP_Y - 36, 68, 9);
      const bbg = ctx.createLinearGradient(0, HOOP_Y - 40, 0, HOOP_Y - 30);
      bbg.addColorStop(0, "#f7f4ea"); bbg.addColorStop(1, "#d9d4c4");
      ctx.fillStyle = bbg;
      ctx.fillRect(g.hoopX - 34, HOOP_Y - 40, 68, 10);
      ctx.strokeStyle = "rgba(60,40,15,0.4)"; ctx.lineWidth = 1;
      ctx.strokeRect(g.hoopX - 34, HOOP_Y - 40, 68, 10);
      ctx.fillStyle = "#C8352E";
      ctx.fillRect(g.hoopX - 10, HOOP_Y - 38, 20, 6);
      ctx.beginPath(); ctx.arc(g.hoopX, HOOP_Y, HOOP_R + 4, 0, Math.PI * 2);
      ctx.fillStyle = "#E8722A"; ctx.fill();
      ctx.strokeStyle = "rgba(90,30,5,0.55)"; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(g.hoopX, HOOP_Y, HOOP_R - 3, 0, Math.PI * 2);
      ctx.fillStyle = "#1d1408"; ctx.fill();
      ctx.strokeStyle = "rgba(255,220,170,0.5)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(g.hoopX, HOOP_Y, HOOP_R + 2.5, Math.PI * 1.05, Math.PI * 1.6); ctx.stroke();
      ctx.lineWidth = 1;
      // net hint
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(g.hoopX + Math.cos(a) * (HOOP_R - 3), HOOP_Y + Math.sin(a) * (HOOP_R - 3));
        ctx.lineTo(g.hoopX + Math.cos(a) * 6, HOOP_Y + Math.sin(a) * 6 + 6);
        ctx.stroke();
      }

      // balls waiting in the tray
      const inTray = LEVELS[g.level].balls - g.balls.length - (g.carry ? 1 : 0);
      for (let i = 0; i < inTray; i++)
        drawBallShape(TRAY.x - (inTray > 2 ? 20 : 11) + i * (inTray > 2 ? 20 : 22), TRAY.y, BALL_R);

      // aim guide
      if (g.aim) {
        const { ball, dx, dy } = g.aim;
        const len = Math.min(Math.hypot(dx, dy), 190);
        if (len > 10 && dy > 0) {
          const ang = Math.atan2(-dy, -dx);
          ctx.strokeStyle = "rgba(255,255,255,0.85)";
          ctx.lineWidth = 3; ctx.setLineDash([8, 7]);
          ctx.beginPath();
          ctx.moveTo(ball.x, ball.y);
          ctx.lineTo(ball.x + Math.cos(ang) * (60 + len * 1.3), ball.y + Math.sin(ang) * (60 + len * 1.3));
          ctx.stroke(); ctx.setLineDash([]); ctx.lineWidth = 1;
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.fillRect(W / 2 - 60, H - 24, 120, 10);
          ctx.fillStyle = len / 190 > 0.75 ? "#D93A2B" : "#E3B923";
          ctx.fillRect(W / 2 - 60, H - 24, 120 * (len / 190), 10);
        }
      }

      // carried ball
      if (g.carry) {
        ctx.beginPath(); ctx.arc(g.carry.x, g.carry.y + 6, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.fill();
        drawBallShape(g.carry.x, g.carry.y - 4, BALL_R * 1.15);
      }

      // balls
      for (const b of g.balls) {
        if (b.phase === "sinking") {
          const k = 1 - Math.min(b.sink.t / 0.4, 1);
          if (k > 0) drawBallShape(b.sink.hole.x, b.sink.hole.y + (1 - k) * 8, BALL_R * k);
          continue;
        }
        const lift = b.phase === "air"
          ? Math.sin(Math.PI * Math.min(b.air.t / AIR_TIME, 1)) * 18
          : 0;
        if (b.edgeT) ctx.globalAlpha = Math.max(0, 1 - Math.max(0, b.edgeT - 0.25) / 0.25);
        ctx.beginPath();
        ctx.arc(b.x, b.y + 3 + lift * 0.5, BALL_R * (1 - lift / 90), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.fill();
        drawBallShape(b.x, b.y - lift, BALL_R * (1 + lift / 60));
        ctx.globalAlpha = 1;
      }

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = 1 - p.t;
        ctx.font = "bold 24px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "#fff";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 34);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 34);
        ctx.globalAlpha = 1;
      }

      // level banner
      if (g.banner) {
        const a = g.banner.t < 1.4 ? 1 : 1 - (g.banner.t - 1.4) / 0.4;
        ctx.globalAlpha = Math.max(0, a);
        let bfs = 22;
        ctx.font = `bold ${bfs}px 'Alfa Slab One', Georgia, serif`;
        const btw = ctx.measureText(g.banner.txt).width;
        if (btw > W - 36) bfs = Math.max(12, Math.floor(bfs * (W - 36) / btw));
        ctx.font = `bold ${bfs}px 'Alfa Slab One', Georgia, serif`;
        ctx.textAlign = "center";
        ctx.lineWidth = 6; ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.strokeText(g.banner.txt, W / 2, 230);
        ctx.fillStyle = "#ffd75e";
        ctx.fillText(g.banner.txt, W / 2, 230);
        ctx.globalAlpha = 1;
      }

      // hint: golden serif, maxWidth clamps long languages clear of the tray
      if (!g.aim && !g.carry && g.balls.length === 0) {
        ctx.font = "600 13px Georgia, 'Times New Roman', serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 3; ctx.strokeStyle = "rgba(40,20,5,0.55)";
        ctx.strokeText(t("b_hint"), (W - 76) / 2, H - 12, W - 96);
        ctx.fillStyle = "#e7c469";
        ctx.fillText(t("b_hint"), (W - 76) / 2, H - 12, W - 96);
        ctx.lineWidth = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (t) => {
      const dt = Math.min((t - last) / 1000, 0.033);
      last = t;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
    };
  }, [phase]);

  /* ================= UI ================= */
  return (
    <div className={"bb-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_basket").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("b_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("b_lede", { n1: LEVELS[0].need, n2: LEVELS[1].need, n3: LEVELS[2].need })}</p>
          <ul className="rules">
            <li>{t("b_rule1", { n: LEVELS[0].need })}</li>
            <li>{t("b_rule2", { n: LEVELS[1].need })}</li>
            <li>{t("b_rule3", { n: LEVELS[2].need })}</li>
          </ul>
          <p className="prizenote">{t("b_prizenote")}</p>
          <button className="cta" onClick={startGame}>{t("b_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{lvlName(hud.level)}: <b>{hud.hits} / {LEVELS[hud.level].need}</b></span>
            <span className="last">{t("b_of", { n: hud.level + 1, max: LEVELS.length })}</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && !claimed && (
        <PrizePicker size="mellem" game="Basketball" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("b_win")}</div>
            <p className="winline">{t("b_winline", { n: LEVELS.length })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {offer && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("ad_offer_cans_title")}</div>
            <p className="winline">{t("ad_offer_basket", { level: lvlName(hud.level) })}</p>
            <button className="cta gold" onClick={watchAd}>{t("ad_watch")}</button>
            {adErr && <p className="adfail">{t("ad_fail")}</p>}
            <button className="cta alt" onClick={declineOffer}>{t("ad_no")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("b_fail")}</div>
            <p className="winline">{t("b_failline", { level: lvlName(failedAt) })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.bb-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#26170c;
  background-image:radial-gradient(circle at 50% -10%, #4a2d16 0%, #26170c 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(24px,7vw,42px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.adfail{font-size:12px; font-weight:800; opacity:.7; margin:8px 0 0;}
.cta.alt{margin-top:10px; background:#7a5a3a; box-shadow:0 4px 0 #4a3520; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #4a3520;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #5a3520; box-shadow:0 12px 30px rgba(0,0,0,.55);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(20,10,4,.72); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#4a3520;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
